import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { Customer, Ticket, TicketDetail } from '@aviaconsole/shared';
import { capi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { slaLabel } from '../lib/format';
import { useToast } from '../components/Toast';
import { Button, Card, Chip, Empty, Input, Loading, Modal, PageHeader, Select, Textarea, cap, priorityTone, ticketStatusLabel, ticketStatusTone } from '../components/ui';

export function Tickets() {
  const { company } = useAuth();
  const { t, s, lang } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['tickets', company?.id], queryFn: () => capi<Ticket[]>('/tickets') });
  const customers = useQuery({ queryKey: ['customers', company?.id], queryFn: () => capi<Customer[]>('/customers') });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customerId: '', subject: '', body: '', priority: 'normal' });
  const create = useMutation({
    mutationFn: () => capi<TicketDetail>('/tickets', { method: 'POST', json: { ...form, customerId: form.customerId || null } }),
    onSuccess: (tk) => { qc.invalidateQueries({ queryKey: ['tickets'] }); setOpen(false); navigate(`/tickets/${tk.id}`); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  return (
    <>
      <PageHeader crumb={t('tckCrumb')} title={t('tckTitle')} sub={t('tckSub')} actions={<Button variant="primary" icon="fas fa-plus" onClick={() => setOpen(true)}>{t('tckAction')}</Button>} />
      <Card pad={false} className="table">
        {!q.data ? <Loading /> : (
          <table>
            <thead><tr><th>{t('colTicket')}</th><th>{t('colSubject')}</th><th>{t('colPriority')}</th><th>{t('colStatus')}</th><th>{t('colAssignee')}</th><th className="r">{t('colSla')}</th></tr></thead>
            <tbody>
              {q.data.length === 0 && <Empty />}
              {q.data.map((tk) => { const sla = slaLabel(tk.firstResponseDueAt, tk.firstRespondedAt, tk.status, lang); return (
                <tr key={tk.id} className="click" onClick={() => navigate(`/tickets/${tk.id}`)}>
                  <td className="ref">{tk.number}</td>
                  <td><div style={{ fontWeight: 600 }}>{tk.subject}</div><div className="sub">{tk.customerName ?? '—'}</div></td>
                  <td><Chip tone={priorityTone(tk.priority)}>{s(cap(tk.priority))}</Chip></td>
                  <td><Chip tone={ticketStatusTone(tk.status)}>{s(ticketStatusLabel(tk.status))}</Chip></td>
                  <td className="secondary small">{tk.assigneeName ?? s('Unassigned')}</td>
                  <td className="r"><Chip tone={sla.tone}>{sla.label}</Chip></td>
                </tr>
              ); })}
            </tbody>
          </table>
        )}
      </Card>
      {open && (
        <Modal title={t('tckAction')} onClose={() => setOpen(false)}>
          <form className="stack md" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            <Select label={t('customerLabel')} value={form.customerId} options={[{ value: '', label: '—' }, ...(customers.data ?? []).map((c) => ({ value: c.id, label: c.name }))]} onChange={(e) => setForm({ ...form, customerId: e.target.value })} />
            <Input label={t('subject')} required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            <Select label={t('priority')} value={form.priority} options={['low', 'normal', 'high'].map((p) => ({ value: p, label: s(cap(p)) }))} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
            <Textarea label={t('message')} required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <div className="row" style={{ justifyContent: 'flex-end' }}><Button onClick={() => setOpen(false)}>{t('cancel')}</Button><Button variant="primary" type="submit" disabled={create.isPending}>{t('create')}</Button></div>
          </form>
        </Modal>
      )}
    </>
  );
}
